import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, FileText, User, X } from 'lucide-react';

export function ReferenceComparison({ check, verificationData, setVerificationData, handleFieldVerification }) {
  const defaultTab = (check?.check_type?.includes('personal_reference') || check?.subcheck_type?.toLowerCase().includes('personal')) ? 'personal' : 'professional';
  const [referenceTab, setReferenceTab] = useState(defaultTab);
    const candidateData = {
      ...(check?.subcheck_data || {}),
      father_name: check?.father_name || check?.subcheck_data?.father_name || '',
      dob: check?.dob || check?.subcheck_data?.dob || check?.subcheck_data?.date_of_birth || '',
      candidate_name: check?.employee_name || check?.subcheck_data?.candidate_name || '',
    };
    
    // Rating options
    const ratingOptions = [
      { value: '5', label: '5 - Excellent', color: 'text-green-600' },
      { value: '4', label: '4 - Very Good', color: 'text-green-500' },
      { value: '3', label: '3 - Good', color: 'text-blue-600' },
      { value: '2', label: '2 - Average', color: 'text-amber-600' },
      { value: '1', label: '1 - Below Average', color: 'text-red-600' },
    ];
    
    // Response options for general dropdown questions
    const responseOptions = [
      { value: 'excellent', label: 'Excellent', color: 'text-green-600 bg-green-50' },
      { value: 'good', label: 'Good', color: 'text-blue-600 bg-blue-50' },
      { value: 'average', label: 'Average', color: 'text-amber-600 bg-amber-50' },
      { value: 'not_applicable', label: 'Not Applicable', color: 'text-slate-500 bg-slate-50' },
      { value: 'not_relevant', label: 'Not Relevant', color: 'text-slate-400 bg-slate-50' },
      { value: 'does_not_want_to_answer', label: 'Does Not Want to Answer', color: 'text-red-500 bg-red-50' },
    ];
    
    // Yes/No dropdown options for specific questions (Personal Q7, Q10; Professional Q10, Q11)
    const yesNoOptions = [
      { value: 'yes', label: 'Yes', color: 'text-green-600 bg-green-50' },
      { value: 'no', label: 'No', color: 'text-red-600 bg-red-50' },
      { value: 'not_applicable', label: 'Not Applicable', color: 'text-slate-500 bg-slate-50' },
      { value: 'not_relevant', label: 'Not Relevant', color: 'text-slate-400 bg-slate-50' },
      { value: 'does_not_want_to_answer', label: 'Does Not Want to Answer', color: 'text-red-500 bg-red-50' },
    ];
    
    // Professional Reference Questions
    const professionalQuestions = [
      { id: 'prof_q1', question: 'How is the Candidate known to you?', type: 'text' },
      { id: 'prof_q2', question: 'How are/were you associated with the Candidate and during what period?', type: 'text' },
      { id: 'prof_q3', question: 'Describe the primary job responsibilities of the candidate during the association period.', type: 'textarea' },
      { id: 'prof_q4', question: 'What are the candidate\'s three strongest qualities?', type: 'textarea' },
      { id: 'prof_q5', question: 'What are the areas that the candidate may focus on for self-improvement?', type: 'textarea' },
      { id: 'prof_q6', question: 'Comment on the candidate\'s interpersonal skills.', type: 'dropdown' },
      { id: 'prof_q7', question: 'How is the candidate\'s general attitude towards complying with rules & regulations?', type: 'dropdown' },
      { id: 'prof_q8', question: 'Comment on the candidate\'s sincerity, integrity & general reputation.', type: 'dropdown' },
      { id: 'prof_q9', question: 'How would you rate the candidate\'s overall performance?', type: 'rating' },
      { id: 'prof_q10', question: 'Would you recommend this candidate for employment?', type: 'yes_no_dropdown' },
      { id: 'prof_q11', question: 'Is the candidate eligible for rehire at your organization?', type: 'yes_no_dropdown' },
    ];
    
    // Personal Reference Questions
    const personalQuestions = [
      { id: 'pers_q1', question: 'How is the Candidate known to you?', type: 'text' },
      { id: 'pers_q2', question: 'How long have you known the candidate?', type: 'text' },
      { id: 'pers_q3', question: 'Describe your relationship with the candidate.', type: 'textarea' },
      { id: 'pers_q4', question: 'What are the candidate\'s strongest personal qualities?', type: 'textarea' },
      { id: 'pers_q5', question: 'Comment on the candidate\'s character and moral values.', type: 'dropdown' },
      { id: 'pers_q6', question: 'How would you describe the candidate\'s behavior in social settings?', type: 'dropdown' },
      { id: 'pers_q7', question: 'Is the candidate trustworthy and reliable?', type: 'yes_no_dropdown' },
      { id: 'pers_q8', question: 'Comment on the candidate\'s family background and reputation in the community.', type: 'textarea' },
      { id: 'pers_q9', question: 'How would you rate the candidate\'s overall character?', type: 'rating' },
      { id: 'pers_q10', question: 'Would you vouch for this candidate?', type: 'yes_no_dropdown' },
    ];
    
    // Clear form function
    const clearReferenceForm = () => {
      const clearedFields = {};
      [...professionalQuestions, ...personalQuestions].forEach(q => {
        clearedFields[q.id] = '';
        clearedFields[`${q.id}_rating`] = '';
      });
      setVerificationData(prev => ({
        ...prev,
        verified_details: {
          ...prev.verified_details,
          ...clearedFields,
          prof_respondent_name: '',
          prof_respondent_designation: '',
          prof_respondent_organization: '',
          prof_respondent_phone: '',
          prof_respondent_email: '',
          prof_contacted_date: '',
          pers_respondent_name: '',
          pers_respondent_relation: '',
          pers_respondent_phone: '',
          pers_respondent_address: '',
          pers_contacted_date: '',
          additional_comments: '',
        }
      }));
      toast.info('Form cleared');
    };
    
    const renderQuestion = (q, prefix) => {
      const fieldKey = q.id;
      const ratingKey = `${q.id}_rating`;
      
      return (
        <div key={q.id} className="p-3 bg-white rounded-lg border shadow-sm">
          <div className="flex items-start justify-between gap-3 mb-2">
            <Label className="text-sm font-medium text-slate-700 flex-1">{q.question}</Label>
            {q.type === 'rating' && (
              <Select 
                value={verificationData.verified_details[ratingKey] || ''} 
                onValueChange={(v) => handleFieldVerification(ratingKey, true, v)}
              >
                <SelectTrigger className="h-8 w-40 text-xs border-amber-200">
                  <SelectValue placeholder="Rate 1-5" />
                </SelectTrigger>
                <SelectContent>
                  {ratingOptions.map(o => (
                    <SelectItem key={o.value} value={o.value}>
                      <span className={o.color}>{o.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          {q.type === 'text' && (
            <Input 
              value={verificationData.verified_details[fieldKey] || ''} 
              onChange={(e) => handleFieldVerification(fieldKey, true, e.target.value)}
              className="h-8 text-sm"
              placeholder="Enter response..."
            />
          )}
          
          {q.type === 'textarea' && (
            <Textarea 
              value={verificationData.verified_details[fieldKey] || ''} 
              onChange={(e) => handleFieldVerification(fieldKey, true, e.target.value)}
              className="h-16 text-sm resize-none"
              placeholder="Enter detailed response..."
            />
          )}
          
          {q.type === 'dropdown' && (
            <Select 
              value={verificationData.verified_details[fieldKey] || 'not_applicable'} 
              onValueChange={(v) => handleFieldVerification(fieldKey, true, v)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select response..." />
              </SelectTrigger>
              <SelectContent>
                {responseOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    <span className={o.color.split(' ')[0]}>{o.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {q.type === 'yes_no_dropdown' && (
            <Select 
              value={verificationData.verified_details[fieldKey] || 'not_applicable'} 
              onValueChange={(v) => handleFieldVerification(fieldKey, true, v)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select response..." />
              </SelectTrigger>
              <SelectContent>
                {yesNoOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    <span className={o.color.split(' ')[0]}>{o.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {q.type === 'rating' && (
            <Textarea 
              value={verificationData.verified_details[fieldKey] || ''} 
              onChange={(e) => handleFieldVerification(fieldKey, true, e.target.value)}
              className="h-12 text-sm resize-none"
              placeholder="Additional comments for rating..."
            />
          )}
        </div>
      );
    };
    
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-3 rounded-lg shadow-lg">
          <h4 className="font-semibold text-base flex items-center gap-2">
            <User className="w-5 h-5" />
            Reference Verification - {check?.subcheck_type || 'Reference Check'}
          </h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearReferenceForm}
            className="bg-white/10 hover:bg-white/20 text-white border-white/30"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Form
          </Button>
        </div>
        
        {/* Candidate Info Banner */}
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="py-3">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-600 font-medium">Candidate Name</span>
                <p className="font-semibold">{candidateData.candidate_name || check?.employee_name || '-'}</p>
              </div>
              <div>
                <span className="text-blue-600 font-medium">Reference Name</span>
                <p className="font-semibold">{candidateData.name || candidateData.reference_name || candidateData.respondent_name || check?.subcheck_name || '-'}</p>
              </div>
              <div>
                <span className="text-blue-600 font-medium">Reference Type</span>
                <p className="font-semibold">{check?.subcheck_type || candidateData.reference_type || 'Professional'}</p>
              </div>
              <div>
                <span className="text-blue-600 font-medium">Contact</span>
                <p className="font-semibold">{candidateData.reference_phone || candidateData.phone || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Reference Type Tabs */}
        <div className="flex gap-2 border-b pb-2">
          <Button 
            variant={referenceTab === 'professional' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setReferenceTab('professional')}
            className={referenceTab === 'professional' ? 'bg-indigo-600' : ''}
          >
            <Briefcase className="w-4 h-4 mr-1" />
            Professional Reference
          </Button>
          <Button 
            variant={referenceTab === 'personal' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setReferenceTab('personal')}
            className={referenceTab === 'personal' ? 'bg-purple-600' : ''}
          >
            <User className="w-4 h-4 mr-1" />
            Personal Reference
          </Button>
        </div>
        
        {/* Professional Reference */}
        {referenceTab === 'professional' && (
          <div className="space-y-4">
            {/* Respondent Details */}
            <Card className="border-2 border-indigo-200">
              <CardHeader className="pb-2 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-t-lg">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Respondent Details (Professional)
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3 pt-4">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Respondent Name</Label>
                  <Input 
                    value={verificationData.verified_details.prof_respondent_name || ''} 
                    onChange={(e) => handleFieldVerification('prof_respondent_name', true, e.target.value)}
                    className="h-8 text-sm"
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Designation</Label>
                  <Input 
                    value={verificationData.verified_details.prof_respondent_designation || ''} 
                    onChange={(e) => handleFieldVerification('prof_respondent_designation', true, e.target.value)}
                    className="h-8 text-sm"
                    placeholder="Job title"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Organization</Label>
                  <Input 
                    value={verificationData.verified_details.prof_respondent_organization || ''} 
                    onChange={(e) => handleFieldVerification('prof_respondent_organization', true, e.target.value)}
                    className="h-8 text-sm"
                    placeholder="Company name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Relationship with Candidate</Label>
                  <Select 
                    value={verificationData.verified_details.prof_respondent_relation || ''} 
                    onValueChange={(v) => {
                      handleFieldVerification('prof_respondent_relation', true, v);
                      // Auto-sync to Q1 "How is the Candidate known to you?"
                      const relationLabel = {
                        manager: 'Manager', supervisor: 'Supervisor', colleague: 'Colleague',
                        hr_representative: 'HR Representative', team_lead: 'Team Lead',
                        reporting_manager: 'Reporting Manager', mentor: 'Mentor',
                        client: 'Client', other: 'Other'
                      }[v] || v;
                      handleFieldVerification('prof_q1', true, relationLabel);
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select relation" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="reporting_manager">Reporting Manager</SelectItem>
                      <SelectItem value="team_lead">Team Lead</SelectItem>
                      <SelectItem value="colleague">Colleague</SelectItem>
                      <SelectItem value="hr_representative">HR Representative</SelectItem>
                      <SelectItem value="mentor">Mentor</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Phone Number</Label>
                  <Input 
                    value={verificationData.verified_details.prof_respondent_phone || ''} 
                    onChange={(e) => handleFieldVerification('prof_respondent_phone', true, e.target.value)}
                    className="h-8 text-sm"
                    placeholder="Phone"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Email</Label>
                  <Input 
                    value={verificationData.verified_details.prof_respondent_email || ''} 
                    onChange={(e) => handleFieldVerification('prof_respondent_email', true, e.target.value)}
                    className="h-8 text-sm"
                    placeholder="Email address"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Date & Time Contacted</Label>
                  <Input 
                    type="datetime-local"
                    value={verificationData.verified_details.prof_contacted_date || ''} 
                    onChange={(e) => handleFieldVerification('prof_contacted_date', true, e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Professional Questionnaire */}
            <Card className="border-2 border-indigo-200">
              <CardHeader className="pb-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-t-lg">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Professional Reference Questionnaire
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-4 bg-gradient-to-b from-indigo-50 to-white">
                {professionalQuestions.map((q, idx) => (
                  <div key={q.id} className="flex gap-2">
                    <span className="text-xs font-bold text-indigo-600 w-6 pt-3">{idx + 1}.</span>
                    <div className="flex-1">
                      {renderQuestion(q, 'prof')}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Personal Reference */}
        {referenceTab === 'personal' && (
          <div className="space-y-4">
            {/* Respondent Details */}
            <Card className="border-2 border-purple-200">
              <CardHeader className="pb-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-t-lg">
                <CardTitle className="text-sm flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Respondent Details (Personal)
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3 pt-4">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Respondent Name</Label>
                  <Input 
                    value={verificationData.verified_details.pers_respondent_name || ''} 
                    onChange={(e) => handleFieldVerification('pers_respondent_name', true, e.target.value)}
                    className="h-8 text-sm"
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Relationship with Candidate</Label>
                  <Select 
                    value={verificationData.verified_details.pers_respondent_relation || ''} 
                    onValueChange={(v) => {
                      handleFieldVerification('pers_respondent_relation', true, v);
                      // Auto-sync to Q1 "How is the Candidate known to you?"
                      const relationLabel = {
                        family_friend: 'Family Friend', neighbor: 'Neighbor', relative: 'Relative',
                        friend: 'Friend', teacher: 'Teacher/Professor', mentor: 'Mentor',
                        community_member: 'Community Member', other: 'Other'
                      }[v] || v;
                      handleFieldVerification('pers_q1', true, relationLabel);
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select relation" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="family_friend">Family Friend</SelectItem>
                      <SelectItem value="neighbor">Neighbor</SelectItem>
                      <SelectItem value="relative">Relative</SelectItem>
                      <SelectItem value="friend">Friend</SelectItem>
                      <SelectItem value="teacher">Teacher/Professor</SelectItem>
                      <SelectItem value="mentor">Mentor</SelectItem>
                      <SelectItem value="community_member">Community Member</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Phone Number</Label>
                  <Input 
                    value={verificationData.verified_details.pers_respondent_phone || ''} 
                    onChange={(e) => handleFieldVerification('pers_respondent_phone', true, e.target.value)}
                    className="h-8 text-sm"
                    placeholder="Phone"
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs font-medium">Address</Label>
                  <Input 
                    value={verificationData.verified_details.pers_respondent_address || ''} 
                    onChange={(e) => handleFieldVerification('pers_respondent_address', true, e.target.value)}
                    className="h-8 text-sm"
                    placeholder="Respondent address"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Date & Time Contacted</Label>
                  <Input 
                    type="datetime-local"
                    value={verificationData.verified_details.pers_contacted_date || ''} 
                    onChange={(e) => handleFieldVerification('pers_contacted_date', true, e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Personal Questionnaire */}
            <Card className="border-2 border-purple-200">
              <CardHeader className="pb-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-t-lg">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Personal Reference Questionnaire
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-4 bg-gradient-to-b from-purple-50 to-white">
                {personalQuestions.map((q, idx) => (
                  <div key={q.id} className="flex gap-2">
                    <span className="text-xs font-bold text-purple-600 w-6 pt-3">{idx + 1}.</span>
                    <div className="flex-1">
                      {renderQuestion(q, 'pers')}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Additional Comments */}
        <Card className="border-2 border-slate-200">
          <CardHeader className="pb-2 bg-slate-100">
            <CardTitle className="text-sm text-slate-700">Additional Comments</CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            <Textarea 
              value={verificationData.verified_details.additional_comments || ''} 
              onChange={(e) => handleFieldVerification('additional_comments', true, e.target.value)}
              className="h-20 text-sm resize-none"
              placeholder="Any additional comments or observations from the reference verification..."
            />
          </CardContent>
        </Card>
      </div>
    );
}
